import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TruckIcon,
  CalendarIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { FormField, DateInput, Select, DateDisplay } from '../../components/Forms';

interface ReportFilters {
  startDate: string;
  endDate: string;
  reportType: 'overview' | 'customers' | 'trips' | 'financial' | 'bookings';
}

const Reports: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      reportType: 'overview'
    };
  });

  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'trips' | 'financial' | 'bookings'>('overview');

  // Overview Report Query
  const { data: overviewData, isLoading: overviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['reports', 'overview', filters.startDate, filters.endDate],
    queryFn: () => apiService.getReportsOverview({
      startDate: filters.startDate,
      endDate: filters.endDate
    }),
    enabled: activeTab === 'overview'
  });

  // Customer Reports Query
  const { data: customerData, isLoading: customerLoading, refetch: refetchCustomers } = useQuery({
    queryKey: ['reports', 'customers', filters.startDate, filters.endDate],
    queryFn: () => apiService.getCustomerReports({
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit: 20
    }),
    enabled: activeTab === 'customers'
  });

  // Trip Reports Query
  const { data: tripData, isLoading: tripLoading, refetch: refetchTrips } = useQuery({
    queryKey: ['reports', 'trips', filters.startDate, filters.endDate],
    queryFn: () => apiService.getTripReports({
      startDate: filters.startDate,
      endDate: filters.endDate
    }),
    enabled: activeTab === 'trips'
  });

  // Financial Reports Query
  const { data: financialData, isLoading: financialLoading, refetch: refetchFinancial } = useQuery({
    queryKey: ['reports', 'financial', filters.startDate, filters.endDate],
    queryFn: () => apiService.getFinancialReports(filters.startDate, filters.endDate),
    enabled: activeTab === 'financial'
  });

  // Booking Analytics Query
  const { data: bookingData, isLoading: bookingLoading, refetch: refetchBookings } = useQuery({
    queryKey: ['reports', 'bookings', filters.startDate, filters.endDate],
    queryFn: () => apiService.getBookingAnalytics({
      startDate: filters.startDate,
      endDate: filters.endDate,
      groupBy: 'day'
    }),
    enabled: activeTab === 'bookings'
  });

  const handleFilterChange = (field: keyof ReportFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
  };

  const refetchCurrentReport = () => {
    switch (activeTab) {
      case 'overview':
        refetchOverview();
        break;
      case 'customers':
        refetchCustomers();
        break;
      case 'trips':
        refetchTrips();
        break;
      case 'financial':
        refetchFinancial();
        break;
      case 'bookings':
        refetchBookings();
        break;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      pending: 'في انتظار التأكيد',
      confirmed: 'مؤكد',
      cancelled: 'ملغي',
      completed: 'مكتمل',
      refunded: 'مسترد'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getTripTypeLabel = (type: string) => {
    const labels = {
      hajj: 'حج',
      umrah: 'عمرة',
      transport: 'نقل'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      cash: 'نقدي',
      card: 'بطاقة',
      transfer: 'تحويل',
      partial: 'مختلط'
    };
    return labels[method as keyof typeof labels] || method;
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: ChartBarIcon },
    { id: 'customers', label: 'العملاء', icon: UsersIcon },
    { id: 'trips', label: 'الرحلات', icon: TruckIcon },
    { id: 'financial', label: 'المالية', icon: CurrencyDollarIcon },
    { id: 'bookings', label: 'الحجوزات', icon: DocumentChartBarIcon }
  ];

  const isLoading = overviewLoading || customerLoading || tripLoading || financialLoading || bookingLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">التقارير</h1>
          <p className="text-gray-600">تقارير شاملة حول أداء النشاط التجاري</p>
        </div>
        <div className="flex items-center space-x-3 space-x-reverse">
          <button
            onClick={refetchCurrentReport}
            className="btn-secondary"
            disabled={isLoading}
          >
            <ArrowDownTrayIcon className="w-5 h-5 ml-2" />
            تحديث البيانات
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DateInput
            label="تاريخ البداية"
            compactToggle
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          <DateInput
            label="تاريخ النهاية"
            compactToggle
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
          <FormField label="نوع التقرير">
            <Select
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value as typeof activeTab)}
              options={[
                { value: 'overview', label: 'نظرة عامة' },
                { value: 'customers', label: 'تقارير العملاء' },
                { value: 'trips', label: 'تقارير الرحلات' },
                { value: 'financial', label: 'التقارير المالية' },
                { value: 'bookings', label: 'تحليل الحجوزات' }
              ]}
            />
          </FormField>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 space-x-reverse px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id as typeof activeTab)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 ml-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="mr-3 text-gray-600">جاري تحميل البيانات...</span>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && overviewData && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <DocumentChartBarIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              إجمالي الحجوزات
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {overviewData.totals?.bookings || 0}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              إجمالي الإيرادات
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.revenue || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              المبلغ المدفوع
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.paidAmount || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              المبلغ المتبقي
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.pendingAmount || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Bookings by Status */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">الحجوزات حسب الحالة</h3>
                      <div className="space-y-3">
                        {overviewData.bookingsByStatus?.map((item: any) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item._id)}`}>
                                {getStatusLabel(item._id)}
                              </span>
                              <span className="mr-3 text-sm text-gray-600">
                                {item.count} حجز
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Trip Types */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">أنواع الرحلات</h3>
                      <div className="space-y-3">
                        {overviewData.tripTypes?.map((item: any) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {getTripTypeLabel(item._id)}
                              </span>
                              <span className="mr-3 text-sm text-gray-600">
                                {item.count} حجز
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">طرق الدفع</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {overviewData.paymentMethods?.map((item: any) => (
                        <div key={item._id} className="bg-white rounded-lg p-4 border">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {getPaymentMethodLabel(item._id)}
                            </span>
                            <span className="text-sm text-gray-600">
                              {item.count} معاملة
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-lg font-bold text-primary-600">
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Customers Tab */}
              {activeTab === 'customers' && customerData && (
                <div className="space-y-6">
                  {/* Customer Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <UsersIcon className="h-8 w-8 text-blue-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.uniqueCustomers || 0}
                          </p>
                          <p className="text-sm text-gray-600">عميل فريد</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <UsersIcon className="h-8 w-8 text-green-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.repeatCustomers || 0}
                          </p>
                          <p className="text-sm text-gray-600">عميل متكرر</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-yellow-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.customerRetentionRate || 0}%
                          </p>
                          <p className="text-sm text-gray-600">معدل الاحتفاظ</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Customers */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">أفضل العملاء</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              العميل
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              عدد الحجوزات
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              إجمالي الإنفاق
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المبلغ المدفوع
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              آخر حجز
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customerData.topCustomers?.map((customer: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {customer._id.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {customer._id.phone}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {customer.bookings}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.totalSpent)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.totalPaid)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <DateDisplay value={customer.lastBooking} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Trips Tab */}
              {activeTab === 'trips' && tripData && (
                <div className="space-y-6">
                  {/* Trip Performance */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">أداء الرحلات</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              رقم الرحلة
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المسار
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الحجوزات
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المسافرون
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              معدل الإشغال
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الإيرادات
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tripData.tripPerformance?.map((trip: any) => (
                            <tr key={trip._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {trip.tripNumber}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.route?.from} → {trip.route?.to}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.bookings}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.passengers} / {trip.capacity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2 ml-3">
                                    <div
                                      className="bg-primary-600 h-2 rounded-full"
                                      style={{ width: `${Math.min(trip.occupancyRate || 0, 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-gray-900">
                                    {Math.round(trip.occupancyRate || 0)}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(trip.revenue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Route Popularity */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">شعبية المسارات</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {tripData.routePopularity?.map((route: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">
                                {route._id.from} → {route._id.to}
                              </p>
                              <p className="text-sm text-gray-600">
                                {route.bookings} حجز • {route.passengers} مسافر
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">
                                {formatCurrency(route.revenue)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Financial Tab */}
              {activeTab === 'financial' && financialData && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">
                      التقارير المالية التفصيلية قيد التطوير. يمكنك الاطلاع على البيانات المالية من خلال تقرير النظرة العامة.
                    </p>
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && bookingData && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">
                      تحليلات الحجوزات التفصيلية قيد التطوير. يمكنك الاطلاع على إحصائيات الحجوزات من خلال تقرير النظرة العامة.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports; 